/**
 * Verschattung (Kapitel N).
 *
 * Die Spec ordnet das als spaetere Stufe ein und beschreibt als Fernziel ein
 * Gelaendemodell mit LiDAR-Huellenlinien. Was hier steht, ist die Vorstufe, die
 * ausdruecklich zuerst gebaut werden sollte (PV-N01): Verschattungsobjekte von
 * Hand setzen — Baum, Nachbargebaeude, Kamin — und den Schattenwurf daraus
 * rechnen.
 *
 * Das kommt ohne gekaufte Daten aus und deckt die haeufigen Faelle ab. Der
 * Grenzwert, um den es geht, wurde im Gespraech mit rund 30 Minuten am Tag und
 * 30 Stunden im Jahr angegeben.
 *
 * Was hier NICHT passiert: Diffusverschattung, Reflexion, Verluste durch
 * Teilverschattung eines Strings. Das Ergebnis ist "dieses Modul liegt zu
 * dieser Stunde im Schatten", nicht "die Anlage verliert x Prozent".
 */
import { kosinusEinfallswinkel, sonnenstandZurOrtszeit, tagImJahr } from './sonne.js';
import { punktInnerhalb } from './polygon.js';
import { gradInBogen } from './typen.js';
/**
 * Schattenwurf eines Objekts zu einem Sonnenstand.
 *
 * Zurueck kommt das Vieleck, das der Koerper auf die Bezugshoehe wirft — also
 * die Grundflaeche plus ihre in Schattenrichtung verschobene Kopie, zu einer
 * Huelle verbunden.
 *
 * @param bezugshoeheM Hoehe der Flaeche, auf die geschattet wird.
 */
export function schattenwurf(objekt, sonnenhoeheGrad, sonnenazimutGrad, bezugshoeheM = 0) {
    if (sonnenhoeheGrad <= 0.5)
        return undefined;
    const wirksameHoehe = objekt.hoeheM - bezugshoeheM;
    if (wirksameHoehe <= 0)
        return undefined;
    // Der Schatten faellt von der Sonne weg. Azimut plus 180 Grad.
    const richtung = gradInBogen(sonnenazimutGrad + 180);
    const laenge = wirksameHoehe / Math.tan(gradInBogen(sonnenhoeheGrad));
    const versatz = { x: Math.sin(richtung) * laenge, y: Math.cos(richtung) * laenge };
    const verschoben = objekt.grundflaeche.map((p) => ({ x: p.x + versatz.x, y: p.y + versatz.y }));
    return konvexeHuelle([...objekt.grundflaeche, ...verschoben]);
}
/** Konvexe Huelle (Andrew). Aus Fuss und Spitze wird damit ein Schattenkoerper. */
export function konvexeHuelle(punkte) {
    if (punkte.length < 3)
        return [...punkte];
    const sortiert = [...punkte].sort((a, b) => a.x - b.x || a.y - b.y);
    const kreuz = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const unten = [];
    for (const p of sortiert) {
        while (unten.length >= 2 && kreuz(unten[unten.length - 2], unten[unten.length - 1], p) <= 0) {
            unten.pop();
        }
        unten.push(p);
    }
    const oben = [];
    for (let i = sortiert.length - 1; i >= 0; i--) {
        const p = sortiert[i];
        while (oben.length >= 2 && kreuz(oben[oben.length - 2], oben[oben.length - 1], p) <= 0) {
            oben.pop();
        }
        oben.push(p);
    }
    unten.pop();
    oben.pop();
    return [...unten, ...oben];
}
/** Grenzwerte aus dem Gespraech: rund 30 Minuten am Tag, 30 Stunden im Jahr. */
export const GRENZWERT_MINUTEN_TAG = 30;
export const GRENZWERT_STUNDEN_JAHR = 30;
/**
 * Verschattungsstunden je Modul ueber ein Jahr (PV-N02, PV-N03).
 *
 * Gerechnet wird auf einem Raster aus Tagen und Stunden. Der Vorgabewert
 * (jeder fuenfte Tag, Viertelstundenschritte) ist der Kompromiss, der auf einem
 * Tablet in ein paar Sekunden durchlaeuft und die Groessenordnung trifft.
 */
export function verschattungsstunden(eingabe, tagesschritt = 5) {
    const schritt = eingabe.schrittStunden ?? 0.25;
    const von = eingabe.vonStunde ?? 4;
    const bis = eingabe.bisStunde ?? 21;
    const stundenJeModul = new Map();
    const maxMinutenAmTag = new Map();
    let gerechneteStunden = 0;
    for (let tag = 1; tag <= 365; tag += tagesschritt) {
        const minutenHeute = new Map();
        for (let stunde = von; stunde <= bis; stunde += schritt) {
            const stand = sonnenstandZurOrtszeit(eingabe.breiteGrad, tag, stunde);
            if (stand.hoeheGrad <= 0.5)
                continue;
            gerechneteStunden += schritt;
            // Geschattet wird auf die Höhe des Moduls, nicht auf den Boden: Ein
            // 10 m hoher Baum wirft auf ein Modul in 6 m Höhe nur den Schatten von
            // 4 m. Module einer Reihe liegen gleich hoch — deshalb je Höhe einmal.
            const schattenJeHoehe = new Map();
            const schattenAuf = (hoeheM) => {
                const schluessel = Math.round(hoeheM * 10) / 10;
                let ringe = schattenJeHoehe.get(schluessel);
                if (!ringe) {
                    ringe = eingabe.objekte
                        .map((objekt) => schattenwurf(objekt, stand.hoeheGrad, stand.azimutGrad, schluessel))
                        .filter((ring) => ring !== undefined && ring.length >= 3);
                    schattenJeHoehe.set(schluessel, ringe);
                }
                return ringe;
            };
            for (const modul of eingabe.module) {
                const schatten = schattenAuf(modul.hoeheM ?? 0);
                if (schatten.some((ring) => punktInnerhalb(modul.mitte, ring))) {
                    // Ein Tag steht stellvertretend fuer die uebersprungenen mit.
                    stundenJeModul.set(modul.schluessel, (stundenJeModul.get(modul.schluessel) ?? 0) + schritt * tagesschritt);
                    minutenHeute.set(modul.schluessel, (minutenHeute.get(modul.schluessel) ?? 0) + schritt * 60);
                }
            }
        }
        for (const [schluessel, minuten] of minutenHeute) {
            if (minuten > (maxMinutenAmTag.get(schluessel) ?? 0)) {
                maxMinutenAmTag.set(schluessel, minuten);
            }
        }
    }
    const ueberGrenzwert = [...stundenJeModul.entries()]
        .filter(([schluessel, stunden]) => stunden > GRENZWERT_STUNDEN_JAHR ||
        (maxMinutenAmTag.get(schluessel) ?? 0) > GRENZWERT_MINUTEN_TAG)
        .map(([schluessel]) => schluessel);
    return { stundenJeModul, maxMinutenAmTag, ueberGrenzwert, gerechneteStunden };
}
/**
 * Schattenwurf fuer einen einzelnen Zeitpunkt — fuer die Darstellung auf der
 * Karte (PV-N02). Schnellauswahl: 21. Dezember, 21. Maerz, 21. Juni.
 */
export function schattenZuZeitpunkt(objekte, breiteGrad, monat, tag, stunde) {
    const tagNummer = tagImJahr(monat, tag);
    const stand = sonnenstandZurOrtszeit(breiteGrad, tagNummer, stunde);
    const schatten = objekte
        .map((objekt) => schattenwurf(objekt, stand.hoeheGrad, stand.azimutGrad))
        .filter((ring) => ring !== undefined && ring.length >= 3);
    return { stand, schatten };
}
/**
 * Aus verschatteten Stunden einen Ertragsabschlag je Flaeche schaetzen.
 *
 * Bewusst grob: Verschattete Stunden liegen ueberwiegend am Rand des Tages, wo
 * ohnehin wenig Ertrag anfaellt. Deshalb wird jede verschattete Stunde mit dem
 * Einstrahlungsgewicht ihres Sonnenstands verrechnet und nicht eins zu eins.
 * Das Ergebnis ersetzt den pauschalen Abschlag aus PV-K03.
 */
export function abschlagAusVerschattung(ergebnis, anzahlModule, breiteGrad, neigungGrad, azimutGrad) {
    if (anzahlModule === 0 || ergebnis.stundenJeModul.size === 0)
        return 0;
    // Wie viel gewichtete Sonnenstunden hat ein Jahr an diesem Ort — und wie
    // viele Stunden steht die Sonne überhaupt am Himmel?
    let gewichtGesamt = 0;
    let sonnenstunden = 0;
    for (let tag = 1; tag <= 365; tag += 5) {
        for (let stunde = 4; stunde <= 21; stunde += 0.25) {
            const stand = sonnenstandZurOrtszeit(breiteGrad, tag, stunde);
            if (stand.hoeheGrad <= 0.5)
                continue;
            sonnenstunden += 0.25 * 5;
            gewichtGesamt += Math.max(0, kosinusEinfallswinkel(stand, neigungGrad, azimutGrad)) * 0.25 * 5;
        }
    }
    if (gewichtGesamt <= 0 || sonnenstunden <= 0)
        return 0;
    const verschattet = [...ergebnis.stundenJeModul.values()].reduce((a, b) => a + b, 0);
    // Mittleres Gewicht einer Sonnenstunde, mal den verschatteten Stunden,
    // geteilt durch das Jahresgewicht aller Module. Geteilt wird durch die
    // Stunden mit Sonne, nicht durch das ganze Fenster von 4 bis 21 Uhr — die
    // Nachtstunden darin drückten den Abschlag um ein Viertel.
    const mittleresGewicht = gewichtGesamt / sonnenstunden;
    const anteil = (verschattet * mittleresGewicht) / (anzahlModule * gewichtGesamt);
    return Math.min(100, Math.max(0, anteil * 100));
}
//# sourceMappingURL=verschattung.js.map